import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/articles';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group cursor-pointer"
          >
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              <span className="text-background-50 font-heading text-lg font-bold">蜜</span>
            </div>
            <span className="font-heading text-xl text-foreground-900 whitespace-nowrap">
              蜜桃网
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/') && location.pathname === '/'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            <div className="relative group">
              <button className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1">
                分类
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              <div className="absolute top-full left-0 mt-1 w-48 bg-background-50 border border-background-200/70 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-base text-primary-500`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/about')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </div>

          {/* Search & Mobile Toggle */}
          <div className="flex items-center gap-2">
            {/* Desktop Search */}
            <div className="hidden md:flex items-center">
              {isSearchOpen ? (
                <div className="flex items-center gap-2 animate-in">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索文章..."
                    className="w-48 px-3 py-1.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    autoFocus
                  />
                  <button
                    onClick={() => { setIsSearchOpen(false); setSearchQuery(''); }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-search-line text-lg"></i>
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors md:hidden cursor-pointer"
            >
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 bg-background-50 py-3 animate-in">
            <div className="px-2 mb-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索文章..."
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
            <Link
              to="/"
              className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-3 text-lg"></i>
              首页
            </Link>
            <div className="px-4 py-2.5 text-sm font-medium text-foreground-500">
              <i className="ri-folders-line mr-3 text-lg"></i>
              内容分类
            </div>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="flex items-center pl-10 pr-4 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`${cat.icon} mr-3 text-base text-primary-500`}></i>
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line mr-3 text-lg"></i>
              关于我们
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
}